import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    background: 'linear-gradient(135deg, #18010a 0%, #4a0e20 50%, #18010a 100%)',
                }}
            >
                <CircularProgress sx={{ color: '#C10B50' }} />
            </Box>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Redirect users with "User" role to votr-access page if they're not on allowed pages
    const userAllowedPages = ['/votr-access', '/content-hub', '/dashboard', '/portfolio', '/details'];
    if (user && user.role.name === 'User' && !userAllowedPages.includes(location.pathname) && !location.pathname.startsWith('/details/')) {
        return <Navigate to="/votr-access" replace />;
    }

    // Check role-based access if requiredRoles are specified
    if (requiredRoles && requiredRoles.length > 0 && user) {
        const hasRequiredRole = requiredRoles.includes(user.role.name);
        if (!hasRequiredRole) {
            // You could redirect to an unauthorized page or show an error
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        flexDirection: 'column',
                        p: 4,
                    }}
                >
                    <h2>Access Denied</h2>
                    <p>You don't have permission to access this page.</p>
                </Box>
            );
        }
    }

    return <>{children}</>;
};

export default ProtectedRoute;